import { useEffect, useState } from 'react'
import { fetchDeployedBundle, isUpdateAvailable } from '../lib/app-version'
import { currentBundle } from '../lib/telemetry'

// A quiet poll: the point is that nobody sits on stale code for days, not that
// they hear about a deploy within seconds.
const POLL_MS = 5 * 60_000
// A tab coming back into focus is the moment worth re-checking, but a person
// flipping between windows must not fire a request every time.
const CHECK_THROTTLE_MS = 60_000
const DISMISSED_KEY = 'finickly:update-dismissed'

function readDismissed(): string | null {
  try {
    return sessionStorage.getItem(DISMISSED_KEY)
  } catch {
    return null
  }
}

/**
 * Tells the person their tab is running yesterday's code.
 *
 * A single-bundle SPA never updates itself: the tab keeps the JavaScript it
 * loaded until someone reloads. That has already cost us twice — a shipped
 * feature looked missing, and an open tab kept asking for a bundle a later
 * deploy had removed. So compare the bundle this tab runs with the one the
 * server hands out now, and offer a reload when they differ.
 */
export function UpdateBanner({ reload }: { reload?: () => void }) {
  const [deployed, setDeployed] = useState<string | null>(null)
  const [dismissed, setDismissed] = useState<string | null>(readDismissed)

  useEffect(() => {
    const running = currentBundle()
    // No hashed bundle → dev server. Nothing to compare, nothing to nag about.
    if (running == null) return

    let cancelled = false
    let lastCheck = 0
    const check = async () => {
      const now = Date.now()
      if (now - lastCheck < CHECK_THROTTLE_MS) return
      lastCheck = now
      const served = await fetchDeployedBundle()
      if (cancelled) return
      if (isUpdateAvailable(running, served)) setDeployed(served)
    }

    void check()
    const timer = setInterval(() => void check(), POLL_MS)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void check()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      cancelled = true
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  if (deployed == null || deployed === dismissed) return null

  return (
    <div
      style={{
        background: 'var(--accent-bg, rgba(59,130,246,0.08))',
        borderBottom: '1px solid rgba(59,130,246,0.3)',
        color: 'var(--fg-1)',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontSize: 13,
      }}
    >
      <span role="img" aria-label="Оновлення">↻</span>
      <div style={{ flex: 1 }}>Доступна нова версія застосунку.</div>
      <button
        type="button"
        onClick={() => (reload ?? (() => window.location.reload()))()}
        style={{
          background: 'var(--accent)',
          border: 'none',
          borderRadius: 4,
          color: '#fff',
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 600,
          padding: '4px 12px',
        }}
      >
        Оновити
      </button>
      <button
        type="button"
        aria-label="Закрити"
        onClick={() => {
          setDismissed(deployed)
          try {
            sessionStorage.setItem(DISMISSED_KEY, deployed)
          } catch {
            // A tab with storage blocked just gets the banner again next check.
          }
        }}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--fg-3)',
          cursor: 'pointer',
          fontSize: 16,
          lineHeight: 1,
          padding: '2px 4px',
        }}
      >
        ✕
      </button>
    </div>
  )
}
